import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { ACCOUNTS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Users, HeartPulse, CheckSquare, Square, PlayCircle, History, Globe2, Zap, ScrollText, CalendarClock, Sparkles, Loader2 } from "lucide-react";

const NONE = "__none__";
const STAGES = ["warmup", "active", "paused", "banned", "shadowbanned"];
const BULK_OPS = ["reanimate", "pause", "resume", "advance_day", "delete"];
const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Asia/Kolkata",
  "Australia/Sydney",
];

const emptyForm = {
  username: "",
  tiktok_url: "",
  device_id: null,
  persona_id: null,
  proxy_id: null,
  timezone: null,
  stage: "warmup",
  warmup_day: 1,
  followers: 0,
  following: 0,
  likes_total: 0,
  videos_posted: 0,
  health_score: 100,
};

export default function Accounts() {
  const [list, setList] = useState([]);
  const [devices, setDevices] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(new Set());
  const [bulkOp, setBulkOp] = useState("reanimate");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [timelineFor, setTimelineFor] = useState(null); // account being viewed in timeline
  const [timeline, setTimeline] = useState(null);
  const [timelineBusy, setTimelineBusy] = useState(false);
  const [adviceFor, setAdviceFor] = useState(null); // account being viewed in advice
  const [advice, setAdvice] = useState(null);
  const [adviceBusy, setAdviceBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, d, p, pr] = await Promise.all([
        api.accounts(), api.devices(), api.personas(), api.proxies(),
      ]);
      setList(a); setDevices(d); setPersonas(p); setProxies(pr);
    } catch { toast.error(t.common.failed); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deviceFor = (aid) => devices.find((d) => d.account_id === aid);
  const personaFor = (id) => personas.find((p) => p.id === id);
  const proxyFor = (id) => proxies.find((p) => p.id === id);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({
      username: a.username || "",
      tiktok_url: a.tiktok_url || "",
      device_id: (devices.find((d) => d.account_id === a.id) || {}).id || null,
      persona_id: a.persona_id || null,
      proxy_id: a.proxy_id || null,
      timezone: a.timezone || null,
      stage: a.stage || "warmup",
      warmup_day: a.warmup_day ?? 1,
      followers: a.followers ?? 0,
      following: a.following ?? 0,
      likes_total: a.likes_total ?? 0,
      videos_posted: a.videos_posted ?? 0,
      health_score: a.health_score ?? 100,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.username) return toast.error("Username обязателен");
    const acctPayload = {
      username: form.username,
      tiktok_url: form.tiktok_url,
      persona_id: form.persona_id || null,
      proxy_id: form.proxy_id || null,
      timezone: form.timezone || null,
      stage: form.stage,
      warmup_day: Number(form.warmup_day) || 0,
      followers: Number(form.followers) || 0,
      following: Number(form.following) || 0,
      likes_total: Number(form.likes_total) || 0,
      videos_posted: Number(form.videos_posted) || 0,
      health_score: Number(form.health_score) || 100,
    };
    try {
      let accountId = editing;
      if (editing) await api.updateAccount(editing, acctPayload);
      else {
        const created = await api.createAccount(acctPayload);
        accountId = created.id;
      }
      const currentlyLinked = devices.find((d) => d.account_id === accountId);
      if (currentlyLinked && currentlyLinked.id !== form.device_id) {
        await api.updateDevice(currentlyLinked.id, { account_id: null });
      }
      if (form.device_id) {
        await api.updateDevice(form.device_id, { account_id: accountId });
      }
      toast.success(editing ? t.common.saved : "Аккаунт добавлен");
      setOpen(false); setForm(emptyForm); setEditing(null);
      load();
    } catch { toast.error(t.common.saveFailed); }
  };

  const del = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try { await api.deleteAccount(id); toast.success(t.common.deleted); load(); }
    catch { toast.error(t.common.failed); }
  };

  const reanimate = async (id) => {
    if (!window.confirm(t.accounts.reanimateConfirm)) return;
    try { await api.reanimateAccount(id); toast.success(t.accounts.reanimated); load(); }
    catch { toast.error(t.common.failed); }
  };

  const openTimeline = async (acc) => {
    setTimelineFor(acc);
    setTimeline(null);
    setTimelineBusy(true);
    try {
      const data = await api.accountTimeline(acc.id, 40);
      setTimeline(data);
    } catch {
      toast.error(t.common.failed);
      setTimelineFor(null);
    } finally {
      setTimelineBusy(false);
    }
  };

  const openAdvice = async (acc) => {
    setAdviceFor(acc);
    setAdvice(null);
    setAdviceBusy(true);
    try {
      const data = await api.aiAccountAdvice(acc.id);
      setAdvice(data);
    } catch {
      toast.error(t.accounts.aiAdviceFailed);
      setAdviceFor(null);
    } finally {
      setAdviceBusy(false);
    }
  };

  // ---- Bulk selection & actions ------------------------------------
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === list.length) setSelected(new Set());
    else setSelected(new Set(list.map((a) => a.id)));
  };
  const clearSel = () => setSelected(new Set());

  const applyBulk = async () => {
    if (selected.size === 0) return;
    const opLabel = t.accounts.bulk.operations[bulkOp];
    if (!window.confirm(t.accounts.bulk.confirm(selected.size, opLabel))) return;
    setBulkBusy(true);
    try {
      const r = await api.bulkAccounts({
        account_ids: Array.from(selected),
        operation: bulkOp,
      });
      toast.success(t.accounts.bulk.done(r.touched, opLabel));
      clearSel();
      load();
    } catch { toast.error(t.common.failed); }
    finally { setBulkBusy(false); }
  };

  const hasSel = selected.size > 0;

  return (
    <div data-testid={ACCOUNTS.root}>
      <PageHeader
        title={t.accounts.title}
        description={t.accounts.description}
        actions={
          <button
            data-testid={ACCOUNTS.addBtn}
            onClick={openNew}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" /> {t.accounts.add}
          </button>
        }
      />

      {/* Bulk action bar */}
      {list.length > 0 && (
        <div
          data-testid="accounts-bulk-bar"
          className={`card-soft p-3 mb-4 flex items-center gap-3 flex-wrap transition-colors ${
            hasSel ? "border-emerald-300 bg-emerald-50/40" : ""
          }`}
        >
          <button
            data-testid="accounts-select-all"
            onClick={toggleAll}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-emerald-300 text-slate-700 font-medium transition-colors"
          >
            {selected.size === list.length && list.length > 0 ? (
              <CheckSquare className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Square className="h-3.5 w-3.5" />
            )}
            {t.common.selectAll}
          </button>
          <div className="text-xs text-slate-500">{t.common.selected(selected.size)}</div>
          <div className="flex-1" />
          <div className="text-xs text-slate-500">{t.accounts.bulk.title}:</div>
          <Select value={bulkOp} onValueChange={setBulkOp}>
            <SelectTrigger className="w-52 h-9" data-testid="accounts-bulk-op">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BULK_OPS.map((op) => (
                <SelectItem key={op} value={op}>{t.accounts.bulk.operations[op]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            data-testid="accounts-bulk-apply"
            onClick={applyBulk}
            disabled={!hasSel || bulkBusy}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <PlayCircle className="h-3.5 w-3.5" /> {t.accounts.bulk.apply}
          </button>
          {hasSel && (
            <button
              onClick={clearSel}
              className="text-xs text-slate-500 hover:text-slate-900 transition-colors"
            >
              {t.common.clearSelection}
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : list.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <Users className="h-10 w-10 text-slate-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.accounts.empty}</div>
          <div className="text-sm text-slate-500 mt-1">{t.accounts.emptyHint}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {list.map((a) => {
            const persona = personaFor(a.persona_id);
            const proxy = proxyFor(a.proxy_id);
            const device = deviceFor(a.id);
            const isSelected = selected.has(a.id);
            const showReanimate = a.stage === "banned" || a.stage === "shadowbanned";
            return (
              <div
                key={a.id}
                className={`card-soft p-5 transition-colors ${isSelected ? "ring-2 ring-emerald-400" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <button
                    data-testid={`account-select-${a.id}`}
                    onClick={() => toggleOne(a.id)}
                    className="mt-1 shrink-0 text-slate-400 hover:text-emerald-500 transition-colors"
                    aria-label="select"
                  >
                    {isSelected ? (
                      <CheckSquare className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </button>
                  {persona ? (
                    <img src={persona.avatar_url} className="h-12 w-12 rounded-2xl object-cover" alt="" />
                  ) : (
                    <div className="h-12 w-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
                      <Users className="h-5 w-5" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-semibold text-slate-900 truncate">{a.username}</div>
                      <StatusBadge status={a.stage} />
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {persona ? `${persona.name} · ${persona.geo}` : "без персоны"}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      data-testid={ACCOUNTS.aiAdviceBtn(a.id)}
                      onClick={() => openAdvice(a)}
                      className="text-slate-400 hover:text-emerald-600 transition-colors p-1"
                      title={t.accounts.aiAdvice}
                    >
                      <Sparkles className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={ACCOUNTS.timelineBtn(a.id)}
                      onClick={() => openTimeline(a)}
                      className="text-slate-400 hover:text-emerald-600 transition-colors p-1"
                      title={t.accounts.timeline}
                    >
                      <History className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={ACCOUNTS.editBtn(a.id)}
                      onClick={() => openEdit(a)}
                      className="text-slate-400 hover:text-emerald-600 transition-colors p-1"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={ACCOUNTS.deleteBtn(a.id)}
                      onClick={() => del(a.id)}
                      className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                  <Stat label={t.accounts.fields.followers} value={a.followers} />
                  <Stat label={t.accounts.fields.following} value={a.following} />
                  <Stat label={t.accounts.fields.likes} value={a.likes_total} />
                  <Stat label={t.accounts.fields.videosPosted} value={a.videos_posted} />
                </div>

                <div className="mt-4 space-y-1 text-xs">
                  <Row label={t.accounts.fields.warmupDay} value={`${a.warmup_day} / 7`} />
                  <Row label={t.accounts.fields.device} value={device ? device.name : t.common.unlinked} />
                  <Row label={t.accounts.fields.proxy} value={proxy ? `${proxy.provider} · ${proxy.geo}` : t.common.unlinked} />
                  <Row
                    label={
                      <span className="inline-flex items-center gap-1">
                        <Globe2 className="h-3 w-3" /> {t.accounts.fields.timezone}
                      </span>
                    }
                    value={a.timezone || <span className="text-slate-400">{t.accounts.timezoneAuto}</span>}
                  />
                  <Row label={t.accounts.fields.health} value={
                    <span className={a.health_score >= 80 ? "text-emerald-600" : a.health_score >= 60 ? "text-amber-600" : "text-rose-600"}>
                      {a.health_score}%
                    </span>
                  } />
                </div>

                {showReanimate && (
                  <button
                    data-testid={ACCOUNTS.reanimateBtn(a.id)}
                    onClick={() => reanimate(a.id)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium transition-colors"
                  >
                    <HeartPulse className="h-3.5 w-3.5" /> {t.accounts.reanimate}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? t.accounts.edit : t.accounts.add}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.username}</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="@mytiktok" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.tiktokUrl}</Label>
              <Input value={form.tiktok_url} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.stage}</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s} value={s}>{t.accounts.stage[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.warmupDay}</Label>
              <Input type="number" value={form.warmup_day} onChange={(e) => setForm({ ...form, warmup_day: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.device}</Label>
              <Select value={form.device_id || NONE} onValueChange={(v) => setForm({ ...form, device_id: v === NONE ? null : v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder={t.common.unlinked} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t.common.unlinked}</SelectItem>
                  {devices.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.model})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.persona}</Label>
              <Select value={form.persona_id || NONE} onValueChange={(v) => setForm({ ...form, persona_id: v === NONE ? null : v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder={t.common.unlinked} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t.common.unlinked}</SelectItem>
                  {personas.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.geo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.proxy}</Label>
              <Select value={form.proxy_id || NONE} onValueChange={(v) => setForm({ ...form, proxy_id: v === NONE ? null : v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder={t.common.unlinked} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t.common.unlinked}</SelectItem>
                  {proxies.map((p) => <SelectItem key={p.id} value={p.id}>{p.provider} · {t.proxies.type[p.type]} · {p.geo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <Globe2 className="h-3 w-3" /> {t.accounts.fields.timezone}
              </Label>
              <Select
                value={form.timezone || NONE}
                onValueChange={(v) => setForm({ ...form, timezone: v === NONE ? null : v })}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder={t.accounts.timezoneAuto} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t.accounts.timezoneAuto}</SelectItem>
                  {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-400 mt-1">{t.accounts.timezoneHint}</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.followers}</Label>
              <Input type="number" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.following}</Label>
              <Input type="number" value={form.following} onChange={(e) => setForm({ ...form, following: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.likes}</Label>
              <Input type="number" value={form.likes_total} onChange={(e) => setForm({ ...form, likes_total: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.videosPosted}</Label>
              <Input type="number" value={form.videos_posted} onChange={(e) => setForm({ ...form, videos_posted: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.accounts.fields.health}</Label>
              <Input type="number" min={0} max={100} value={form.health_score} onChange={(e) => setForm({ ...form, health_score: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid={ACCOUNTS.saveBtn}
              onClick={submit}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              {editing ? t.common.saveChanges : t.common.save}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!timelineFor} onOpenChange={(o) => { if (!o) { setTimelineFor(null); setTimeline(null); } }}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <History className="h-5 w-5 text-emerald-500" />
              {timelineFor ? t.accounts.timelineTitle(timelineFor.username) : ""}
            </DialogTitle>
          </DialogHeader>
          {timelineBusy ? (
            <div className="text-sm text-slate-400 py-6 text-center">{t.common.loading}</div>
          ) : timeline ? (
            <TimelineView data={timeline} accountTz={timelineFor?.timezone} />
          ) : null}
          <DialogFooter>
            <button
              data-testid={ACCOUNTS.timelineClose}
              onClick={() => { setTimelineFor(null); setTimeline(null); }}
              className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium transition-colors"
            >
              {t.common.close}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adviceFor} onOpenChange={(o) => { if (!o) { setAdviceFor(null); setAdvice(null); } }}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-500" />
              {adviceFor ? t.accounts.aiAdviceTitle(adviceFor.username) : ""}
            </DialogTitle>
          </DialogHeader>
          {adviceBusy ? (
            <div className="text-sm text-slate-500 py-8 text-center flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> {t.accounts.aiAdviceLoading}
            </div>
          ) : advice ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">{t.accounts.aiAdviceRisk}:</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  advice.risk === "high" ? "bg-rose-100 text-rose-700"
                  : advice.risk === "medium" ? "bg-amber-100 text-amber-700"
                  : "bg-emerald-100 text-emerald-700"
                }`}>
                  {t.accounts[`aiAdviceRisk_${advice.risk}`] || advice.risk}
                </span>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t.accounts.aiAdviceSummary}</div>
                <div className="text-sm text-slate-800 leading-relaxed">{advice.summary || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{t.accounts.aiAdviceTips}</div>
                <ul className="space-y-1.5">
                  {(advice.advice || []).map((tip, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-700">
                      <span className="text-emerald-500 shrink-0">{i + 1}.</span>
                      <span className="leading-snug">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <button
              data-testid={ACCOUNTS.aiAdviceClose}
              onClick={() => { setAdviceFor(null); setAdvice(null); }}
              className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium transition-colors"
            >
              {t.common.close}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TimelineView({ data, accountTz }) {
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const opts = { hour: "2-digit", minute: "2-digit", month: "short", day: "2-digit" };
      if (accountTz) opts.timeZone = accountTz;
      return d.toLocaleString(undefined, opts);
    } catch { return iso; }
  };
  const totalEmpty =
    (!data.logs || data.logs.length === 0) &&
    (!data.upcoming_warmup || data.upcoming_warmup.length === 0) &&
    (!data.upcoming_schedule || data.upcoming_schedule.length === 0);

  if (totalEmpty) {
    return <div className="text-sm text-slate-500 text-center py-6">{t.accounts.timelineEmpty}</div>;
  }

  return (
    <div className="space-y-5">
      {(data.upcoming_warmup?.length > 0 || data.upcoming_schedule?.length > 0) && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
            <CalendarClock className="h-3 w-3" /> {t.accounts.timelineUpcoming}
          </div>
          <div className="space-y-1.5">
            {(data.upcoming_warmup || []).map((w) => (
              <div key={`w-${w.id}`} className="flex items-start gap-2 text-xs">
                <Zap className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <div className="text-slate-500 shrink-0 w-28 font-mono">{fmt(w.planned_at)}</div>
                <div className="text-slate-800">
                  <span className="text-emerald-600 font-medium">{t.accounts.timelineWarmup}</span>
                  {" · "}{w.action}
                </div>
              </div>
            ))}
            {(data.upcoming_schedule || []).map((s) => (
              <div key={`s-${s.id}`} className="flex items-start gap-2 text-xs">
                <CalendarClock className="h-3.5 w-3.5 text-sky-500 mt-0.5 shrink-0" />
                <div className="text-slate-500 shrink-0 w-28 font-mono">{fmt(s.scheduled_at)}</div>
                <div className="text-slate-800">
                  <span className="text-sky-600 font-medium">{t.accounts.timelineSchedule}</span>
                  {" · "}{s.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.logs?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
            <ScrollText className="h-3 w-3" /> {t.accounts.timelineRecent}
          </div>
          <div className="space-y-1.5">
            {data.logs.map((l) => (
              <div key={l.id} className="flex items-start gap-2 text-xs">
                <span
                  className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${
                    l.level === "error"
                      ? "bg-rose-500"
                      : l.level === "warn"
                      ? "bg-amber-500"
                      : l.level === "success"
                      ? "bg-emerald-500"
                      : "bg-slate-400"
                  }`}
                />
                <div className="text-slate-500 shrink-0 w-28 font-mono">{fmt(l.ts)}</div>
                <div className="text-slate-800 flex-1 min-w-0">
                  <span className="font-medium text-slate-900">{l.action}</span>
                  {l.detail ? <span className="text-slate-600"> — {l.detail}</span> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg py-2">
      <div className="font-display text-lg font-semibold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium">{value}</span>
    </div>
  );
}
